import { Button, Input, InputWrapper } from '@vrplatform/components';
import {
  captureSentryError,
  showApiErrorNotification,
  useApiClient,
  useTeam,
} from '@vrplatform/data';
import { showWarnNotification } from '@vrplatform/theme';
import { useAppName } from '@vrplatform/theme';
import { type Maybe, toTitleCase } from '@vrplatform/utils';
import { Group, Modal, SegmentedControl } from '@mantine/core';
import { Text } from '@mantine/core';
import { Box } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  useDepositDetailDrawer,
  usePaymentDetailDrawer,
  useReservationDetailDrawer,
} from '../../drawers';
import { useAutomationHistoryDrawer } from '../../hooks';
import { useListingDetailDrawer } from '../../views/listings/drawer';
import { ReservationCreateModal } from './ReservationCreateModal';
import type { ExportSingleImportUniqueRef, ExtractConnection } from './_types';

type Props = {
  connection: Maybe<ExtractConnection>;
  closeModal: () => void;
  uniqueRef: ExportSingleImportUniqueRef;
  onCreate?: (reference?: string) => void;
};

type FormInputs = {
  reference: string;
};

export const ExtractSingleModal = (props: Props) => {
  const [{ isGL }] = useTeam();
  const [modal, setModal] = useState<'reservation' | undefined>(undefined);

  const onCreate = useMemo(() => {
    if (props.uniqueRef === 'reservation' && isGL) {
      return () => setModal('reservation');
    }
    return undefined;
  }, [props.uniqueRef, isGL]);

  return (
    <>
      <Extract
        {...props}
        connection={props.connection && !modal ? props.connection : undefined}
        onCreate={onCreate}
      />
      <ReservationCreateModal
        opened={modal === 'reservation'}
        closeModal={() => {
          setModal(undefined);
          props.closeModal();
        }}
        openExtractModal={() => setModal(undefined)}
      />
    </>
  );
};

const Extract = ({
  connection,
  closeModal: cl,
  uniqueRef,
  onCreate,
}: Props) => {
  const [{ isGL }] = useTeam();
  const { appName } = useAppName();
  const methods = useForm<FormInputs>();
  const $api = useApiClient();
  const [inputType, setInputType] = useState<'url' | 'code'>('url');

  const { open: openReservationDetailDrawer } = useReservationDetailDrawer();
  const { open: openListingDetailDrawer } = useListingDetailDrawer();
  const { open: openPaymentDetailDrawer } = usePaymentDetailDrawer();
  const { open: openDepositDetailDrawer } = useDepositDetailDrawer();
  const { open: openAutomationHistoryDrawer } = useAutomationHistoryDrawer();

  const connectionId = connection?.id;

  const allowSwitchLabels =
    uniqueRef === 'reservation' &&
    !!(connection as any)?.allowConfirmationCode &&
    !!connection?.urlExample;

  const onlyCode =
    connection?.allowConfirmationCode &&
    !connection?.urlExample &&
    uniqueRef === 'reservation';

  const closeModal = useCallback(() => {
    cl();
    methods.reset({
      reference: '',
    });
  }, [cl, methods.reset]);

  const submit = useCallback(
    async ({ reference }: FormInputs) => {
      if (!connectionId) {
        captureSentryError('Connection to delete missing');
        showWarnNotification({
          title: 'Missing connection',
          message:
            'Connection to delete missing. Please reach out to support if the issue persists.',
        });

        return;
      }

      const trimmed = (reference || '').trim();

      if (!trimmed) {
        showWarnNotification({
          message: 'Please enter a reference value',
        });

        return;
      }

      const label =
        uniqueRef === 'payout' ? (isGL ? 'deposit' : 'payment') : uniqueRef;

      return await $api
        .POST('/connections/{id}/sync', {
          params: {
            path: {
              id: connectionId,
            },
          },
          body: {
            uniqueRef: trimmed,
            type: uniqueRef,
          },
        })
        .then((result) => {
          if (result.error) {
            if (result.error.code === 'NOT_FOUND') {
              showWarnNotification({
                title: `We couldn't find the ${label}`,
                message:
                  'Please verify that your input is correct and try again.',
                autoClose: 5000,
              });
              return;
            }

            throw result.error;
          }

          const data = result.data;

          const syncId = data.id;

          const isFailed = data.status === 'failed';
          const isReservation = data.type === 'reservation';
          const isListing = data.type === 'listing';
          const isPayout = data.type === 'payout';

          if (isFailed) {
            openAutomationHistoryDrawer(syncId);
          } else if (isReservation) {
            openReservationDetailDrawer(data.reservationId, 'overview');
          } else if (isListing) {
            openListingDetailDrawer(data.listingId, 'overview');
          } else if (isPayout) {
            if (isGL) openDepositDetailDrawer(data.paymentId);
            else openPaymentDetailDrawer(data.paymentId, 'overview');
          } else {
            openAutomationHistoryDrawer(syncId);
          }

          // finally
          closeModal();
        })
        .catch((err) => {
          showApiErrorNotification({
            error: err,
            title: `Failed to fetch ${label}`,
            defaultMessage: `We failed to fetch the ${label}. Please reach out to support if the issue persists.`,
          });
        });
    },
    [
      $api,
      connectionId,
      closeModal,
      openReservationDetailDrawer,
      openAutomationHistoryDrawer,
      openListingDetailDrawer,
      openDepositDetailDrawer,
      openPaymentDetailDrawer,
      uniqueRef,
      isGL,
    ]
  );

  const inputPlaceholder = useMemo(() => {
    if (uniqueRef === 'reservation') {
      if (connection?.urlExample && inputType === 'url')
        return `E.g. ${connection.urlExample}`;

      if (inputType === 'code') return 'Enter reservation confirmation code';
      return 'Enter the reservation URL from your PMS ';
    }

    const label =
      uniqueRef === 'payout' ? (isGL ? 'deposit' : 'payment') : uniqueRef;

    return `Enter your ${label} reference`;
  }, [inputType, connection?.urlExample, uniqueRef, isGL]);

  const inputLabel = useMemo(() => {
    if (uniqueRef === 'reservation') {
      if (inputType === 'url') return 'PMS Reservation URL';
      return 'Confirmation Code';
    }
    return 'Reference';
  }, [inputType, uniqueRef]);

  useEffect(() => {
    if (onlyCode) {
      setInputType('code');
    }
  }, [onlyCode]);

  return (
    <Modal
      opened={!!connection}
      onClose={closeModal}
      size={500}
      title={`Add ${toTitleCase(uniqueRef)}`}
      centered
      styles={{
        title: {
          fontWeight: 500,
        },
      }}
    >
      <Text c="neutral" mb="lg">
        {uniqueRef === 'reservation'
          ? `Use the reservation URL from your PMS to fetch reservations individually. This will create or update the ${uniqueRef} in ${appName}.`
          : `Use the reference of a ${uniqueRef} to fetch it individually. This will create or update the ${uniqueRef} in ${appName}.`}
      </Text>

      <Box
        component="form"
        onSubmit={methods.handleSubmit(submit)}
        onReset={closeModal}
      >
        <Box mt={'xs'} mb={'xl'}>
          {allowSwitchLabels && (
            <SegmentedControl
              name="allowConfirmationCode"
              fullWidth
              value={inputType}
              mb="xs"
              onChange={(v) => setInputType(v as typeof inputType)}
              data={[
                {
                  label: 'PMS Reservation URL ',
                  value: 'url' satisfies typeof inputType,
                },
                {
                  label: 'Confirmation Code',
                  value: 'code' satisfies typeof inputType,
                },
              ]}
            />
          )}
          <Controller
            control={methods.control}
            name="reference"
            rules={{
              validate: (v: string) => {
                const value = (v || '').trim();
                // Check if the input is a valid URL
                if (inputType === 'url' && uniqueRef === 'reservation') {
                  // use a stricter, correct URL regex for validation
                  const reg =
                    /^(https?:\/\/)(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(:\d+)?(\/.*)?$/;
                  const isValid = reg.test(value);

                  if (!isValid) return 'Please enter a valid URL';

                  return true;
                }

                return !!value || 'Reference is required';
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <InputWrapper
                id={field.name}
                label={inputLabel}
                error={error?.message}
              >
                <Input
                  name={field.name}
                  placeholder={inputPlaceholder}
                  type="text"
                  value={field.value || ''}
                  setValue={field.onChange}
                  error={!!error}
                  data-autofocus
                />
              </InputWrapper>
            )}
          />
        </Box>
        <Group justify="right" my={5}>
          {!!onCreate && (
            <Button
              type="button"
              disabled={methods.formState.isSubmitting}
              sx={{
                marginRight: 'auto',
              }}
              onClick={() => onCreate(methods.getValues('reference'))}
            >
              Add manually
            </Button>
          )}

          <Button type="reset" disabled={methods.formState.isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            loading={methods.formState.isSubmitting}
          >
            Submit
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
